import type { ProjectTask, TaskGroup } from '../types';
import type { GroupTimelineSection } from './groupTimeline';
import type { GanttFlatRow } from './ganttSubtaskRows';

export const GANTT_COMPARE_PIN_LIMIT = 6;
export const GANTT_COMPARE_PINS_STORAGE_KEY = 'pm-hub-group-timeline-pins';

export type GanttComparePinState = {
  keys: string[];
};

export function ganttFlatRowPinKey(row: GanttFlatRow): string | null {
  if (row.type === 'section') return row.section.id ? `section:${row.section.id}` : null;
  if (row.type === 'group') return row.group.id ? `group:${row.group.id}` : null;
  if ((row.type === 'task' || row.type === 'subtask') && row.task.id) {
    return `task:${row.task.id}`;
  }
  return null;
}

export function parseGanttPinKey(
  key: string,
): { kind: 'section' | 'group' | 'task'; id: string } | null {
  const idx = key.indexOf(':');
  if (idx <= 0) return null;
  const kind = key.slice(0, idx);
  const id = key.slice(idx + 1);
  if (!id) return null;
  if (kind === 'section' || kind === 'group' || kind === 'task') return { kind, id };
  return null;
}

export function readStoredGanttComparePins(): string[] {
  try {
    const raw = sessionStorage.getItem(GANTT_COMPARE_PINS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) {
      return parsed.filter((k): k is string => typeof k === 'string' && !!parseGanttPinKey(k))
        .slice(0, GANTT_COMPARE_PIN_LIMIT);
    }
    if (parsed && typeof parsed === 'object') {
      const rec = parsed as { keys?: unknown; taskIds?: unknown; rowKeys?: unknown };
      const keys = Array.isArray(rec.keys) ? rec.keys : [];
      const rowKeys = Array.isArray(rec.rowKeys) ? rec.rowKeys : [];
      const taskIds = Array.isArray(rec.taskIds)
        ? rec.taskIds.map((id) => (typeof id === 'string' ? `task:${id}` : ''))
        : [];
      return [...keys, ...rowKeys, ...taskIds]
        .filter((k): k is string => typeof k === 'string' && !!parseGanttPinKey(k))
        .slice(0, GANTT_COMPARE_PIN_LIMIT);
    }
    return [];
  } catch {
    return [];
  }
}

export function storeGanttComparePins(keys: readonly string[]): void {
  try {
    sessionStorage.setItem(
      GANTT_COMPARE_PINS_STORAGE_KEY,
      JSON.stringify({ keys: keys.slice(0, GANTT_COMPARE_PIN_LIMIT) }),
    );
  } catch {
    /* quota / private-mode */
  }
}

export function toggleGanttPinKey(
  keys: readonly string[],
  key: string,
  limit = GANTT_COMPARE_PIN_LIMIT,
): { keys: string[]; atCap: boolean } {
  if (keys.includes(key)) {
    return { keys: keys.filter((k) => k !== key), atCap: false };
  }
  if (keys.length >= limit) {
    return { keys: [...keys], atCap: true };
  }
  return { keys: [...keys, key], atCap: false };
}

export function excludePinnedGanttRows(
  rows: readonly GanttFlatRow[],
  pinKeys: ReadonlySet<string>,
): GanttFlatRow[] {
  if (pinKeys.size === 0) return [...rows];
  return rows.filter((row) => {
    const key = ganttFlatRowPinKey(row);
    return !key || !pinKeys.has(key);
  });
}

function synthesizeTaskRow(task: ProjectTask, childCount: number): GanttFlatRow {
  if (task.parentTaskId) {
    return { type: 'subtask', task, parentId: task.parentTaskId };
  }
  return { type: 'task', task, childCount };
}

export function resolveGanttPinnedRows(params: {
  pinKeys: readonly string[];
  visibleRows: readonly GanttFlatRow[];
  tasks: readonly ProjectTask[];
  groups: readonly TaskGroup[];
  sections?: readonly GroupTimelineSection[];
  childCountByParent?: ReadonlyMap<string, number>;
}): { rows: GanttFlatRow[]; keptKeys: string[] } {
  const { pinKeys, visibleRows, tasks, groups, sections, childCountByParent } = params;
  const visibleByKey = new Map<string, GanttFlatRow>();
  for (const row of visibleRows) {
    const key = ganttFlatRowPinKey(row);
    if (key) visibleByKey.set(key, row);
  }
  const taskById = new Map(tasks.filter((t) => t.id).map((t) => [t.id!, t]));
  const groupById = new Map(groups.filter((g) => g.id).map((g) => [g.id!, g]));
  const sectionById = new Map((sections ?? []).map((s) => [s.id, s]));

  const rows: GanttFlatRow[] = [];
  const keptKeys: string[] = [];
  for (const key of pinKeys) {
    const parsed = parseGanttPinKey(key);
    if (!parsed) continue;
    const fromVisible = visibleByKey.get(key);
    if (fromVisible) {
      rows.push(fromVisible);
      keptKeys.push(key);
      continue;
    }
    if (parsed.kind === 'task') {
      const task = taskById.get(parsed.id);
      if (!task) continue;
      const childCount = childCountByParent?.get(parsed.id) ?? 0;
      rows.push(synthesizeTaskRow(task, childCount));
      keptKeys.push(key);
      continue;
    }
    if (parsed.kind === 'group') {
      const group = groupById.get(parsed.id);
      if (!group) continue;
      const taskCount = tasks.filter((t) => !t.parentTaskId && t.groupId === parsed.id).length;
      rows.push({ type: 'group', group, taskCount });
      keptKeys.push(key);
      continue;
    }
    const section = sectionById.get(parsed.id);
    if (!section) continue;
    const sectionGroupIds = new Set(section.groupIds);
    const taskCount = tasks.filter((t) => !t.parentTaskId && t.groupId && sectionGroupIds.has(t.groupId)).length;
    rows.push({ type: 'section', section, taskCount });
    keptKeys.push(key);
  }
  return { rows, keptKeys };
}
